import {defineStore, storeToRefs} from 'pinia'
import {computed, ref} from "vue";
import { useStatesStore } from "@/store/states.js";
import {useRouter} from "vue-router";

export const useQrCodesStore = defineStore('qrCodes', () => {
    const router = useRouter();
    const statesStore = useStatesStore();
    const qrCodes = ref([]);
    const qrCode = ref(null);
    const newQrCode = ref({
        step: 1,
        name: '',
        homepage_title: '',
        homepage_sub_title: '',
        type: 'webpage',
        redirect_url: '',
        product_id: null,
    });

    const { loading, validationErrors } = storeToRefs(statesStore);

    const fetchAll = async () => {
        loading.value = true

        try {
            const response = await axios.get('/api/qr-codes');
            qrCodes.value = response.data.data;
        } catch (error) { }
    };

    const fetchOne = async (id, force = false) => {
        if (!force && qrCode.value && qrCode.value.id === id) {
            return;
        }

        validationErrors.value = {};
        loading.value = true;

        try {
            const response = await axios.get(`/api/qr-codes/${id}`);
            qrCode.value = response.data.data;
        } catch (error) {
            //
        } finally {
            loading.value = false;
        }
    };

    const remove = async(id) => {
        loading.value = true;

        try {
            await axios.delete(`/api/qr-codes/${id}`);
            await router.push('/dashboard');
        } catch (error) {
            //
        } finally {
            loading.value = false;
        }
    };

    const update = async (callback = null, silent = false) => {
        if (! qrCode.value) {
            return;
        }

        if (! silent) {
            loading.value = true;
        }

        const payload = qrCode.value;

        if (payload.homepage) {
            payload.homepage.title = payload.homepage.title_current;
            payload.homepage.sub_title = payload.homepage.sub_title_current;
        }

        try {
            const response = await axios.put(`/api/qr-codes/${qrCode.value.id}`, payload);
            if (! silent) {
                await statesStore.onSuccess();
            }

            const data = response.data.data;
            data.homepage.title_current = data.homepage.title;
            data.homepage.sub_title_current = data.homepage.sub_title;

            qrCode.value = response.data.data;
            if (typeof callback === 'function') {
                callback();
            }
        } catch (error) {
            await statesStore.onFail();
            validationErrors.value = error.response.data.errors;
        }
    };

    const canEditQrCode = computed(() => {
        // The contents of the QR Chip should not be changed when in any other state thant 'creating'
        return qrCode.value.status === 'creating';
    });

    const create = async (data) => {
        loading.value = true;

        try {
            const response = await axios.post(`/api/qr-codes`, data);
            await statesStore.onSuccess();
            qrCode.value = response.data.data;
        } catch (error) {
            await statesStore.onFail();
            validationErrors.value = error.response.data.errors;
        }
    };

    const resetNewQrCode = () => {
        newQrCode.value = {
            step: 1,
            name: '',
            homepage_title: '',
            homepage_sub_title: '',
            type: 'webpage',
            redirect_url: '',
            product_id: null,
        };

        qrCode.value = null;
    };

    const orderThroughAgency = async (payload) => {
        loading.value = true;

        try {
            await axios.post(`/api/store/order/${qrCode.value.id}`, payload);
            await statesStore.onSuccess();
            await router.push({ name: 'qr-code', query: { order: "success" }}, qrCode.value.id);
        } catch (error) {
            await statesStore.onFail();
            validationErrors.value = error.response.data.errors;
        }
    };

    return {
        qrCodes,
        qrCode,
        newQrCode,
        canEditQrCode,
        fetchAll,
        fetchOne,
        create,
        update,
        remove,
        resetNewQrCode,
        orderThroughAgency,
    }
})
